import { useState, useEffect } from 'react';
import { AlertTriangle, Building2, Sparkles, ShieldCheck, ShieldAlert, ArrowUpRight } from 'lucide-react';
import { api } from '../services/api';
import { qualificarCnae, labelCnae } from '../utils/cnae';
import { formatCnpj } from '../utils/masks';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export default function EmpresaTab({ lead, onOpenConta }) {
  const [conta, setConta] = useState(null);
  const [parentConta, setParentConta] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const contaId = lead?.conta_id;

  useEffect(() => {
    if (!contaId) {
      setConta(null);
      setParentConta(null);
      setFiliais([]);
      return;
    }

    setLoading(true);
    setError('');

    // Fetch account details
    api.getConta(contaId)
      .then(c => {
        setConta(c);
        // Fetch mother account details if it's a child account
        if (c.parent_conta_id) {
          api.getConta(c.parent_conta_id)
            .then(setParentConta)
            .catch(() => {});
        } else {
          setParentConta(null);
        }

        // Fetch registered filiais
        api.getContaFiliais(contaId)
          .then(setFiliais)
          .catch(() => setFiliais([]));
      })
      .catch(err => {
        setError(err.message || 'Erro ao carregar dados da conta');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contaId]);

  if (!contaId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-200">
        <Building2 size={32} className="mb-3 opacity-35" />
        <p className="text-sm font-medium">Vincule uma conta a este lead para ver a ficha da empresa.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-[#00DBFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
        <p className="text-sm text-red-700 font-medium">Erro: {error}</p>
      </div>
    );
  }

  if (!conta) return null;

  const safeJsonParse = (str, fallback = []) => {
    if (!str) return fallback;
    try {
      return typeof str === 'string' ? JSON.parse(str) : str;
    } catch {
      return fallback;
    }
  };

  const qsa = safeJsonParse(conta.qsa_data, []);
  const sitStatus = conta.situacao_cadastral || conta.situacao;
  const isAtiva = sitStatus?.toUpperCase() === 'ATIVA';
  const cnaePrincipalStatus = qualificarCnae(conta.cnae_principal_codigo);

  return (
    <div className="space-y-5">
      {/* ── BANNER AMARELO (Matriz Warning) ── */}
      {conta.parent_conta_id && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 p-3.5 text-amber-800 text-xs flex items-start gap-2.5 shadow-sm transition-all duration-300">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5 animate-pulse" />
          <div className="flex-1">
            <p className="font-bold text-amber-900">Esta empresa é filial</p>
            <p className="text-amber-700 font-medium mt-0.5">
              Esta empresa é filial de <strong className="text-amber-900">{parentConta?.nome || parentConta?.razao_social || 'sua matriz'}</strong>. O decisor estratégico pode estar lá.
            </p>
            <button
              type="button"
              onClick={() => onOpenConta?.(conta.parent_conta_id)}
              className="mt-2 text-amber-900 hover:text-amber-950 font-bold underline inline-flex items-center gap-0.5 cursor-pointer hover:scale-102 transition-transform"
            >
              Ver conta da matriz <ArrowUpRight size={11} />
            </button>
          </div>
        </div>
      )}

      {/* ── BANNER AZUL (Filiais Count) ── */}
      {filiais.length > 0 && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sky-800 text-xs flex items-center gap-2 shadow-sm transition-all duration-300">
          <Building2 size={16} className="text-sky-500 shrink-0" />
          <p className="font-semibold">
            🏢 Esta empresa possui <strong className="text-sky-900">{filiais.length} {filiais.length === 1 ? 'filial cadastrada' : 'filiais cadastradas'}</strong> no CRM.
          </p>
        </div>
      )}

      {/* ── CARD PRINCIPAL ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-[#002233] truncate">{conta.razao_social || conta.nome}</h3>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{formatCnpj(conta.cnpj)}</p>
          </div>
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${
            isAtiva
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            {isAtiva ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />}
            {sitStatus || 'NÃO CONSTA'}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {conta.simples_nacional === 1 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
              Simples Nacional
            </span>
          )}
          {conta.porte && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
              Porte: {conta.porte}
            </span>
          )}
          {conta.capital_social > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
              Capital: {fmtBRL(conta.capital_social)}
            </span>
          )}
        </div>
      </div>

      {/* ── GRID 2 COLUNAS ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CNAE principal */}
        <div className="bg-slate-50 border border-gray-200 rounded-lg p-3">
          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block mb-1">CNAE Principal</span>
          <div className="flex items-start gap-1.5 mt-1.5">
            <span className={`text-[9px] font-bold shrink-0 px-2 py-0.5 rounded-full border ${
              cnaePrincipalStatus === 'aprovado'
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}>
              {labelCnae(cnaePrincipalStatus)}
            </span>
            <div className="min-w-0 text-xs">
              <p className="font-bold text-gray-500 font-mono">{conta.cnae_principal_codigo}</p>
              <p className="text-gray-700 font-semibold truncate mt-0.5">{conta.cnae_principal_descricao || '—'}</p>
            </div>
          </div>
        </div>

        {/* Localização e abertura */}
        <div className="bg-slate-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 flex flex-col justify-between gap-2.5">
          <div>
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block mb-1">Localização</span>
            <p className="font-semibold text-gray-800 mt-1">{[conta.cidade, conta.estado || conta.uf].filter(Boolean).join('/') || '—'}</p>
          </div>
          {conta.data_abertura && (
            <div>
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block mb-1">Abertura</span>
              <p className="font-semibold text-gray-800">{conta.data_abertura}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── SÓCIOS (Quadro Societário) ── */}
      <div>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
          Sócios & Administradores
          <span className="bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.2 text-[9px]">{qsa.length}</span>
        </h4>
        {qsa.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Sócios não informados.</p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-h-36 overflow-y-auto pr-1">
            <div className="divide-y divide-gray-100">
              {qsa.map((s, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50/50 text-xs">
                  <span className="font-semibold text-gray-700 truncate">{s.nome}</span>
                  <span className="text-gray-400 italic font-medium ml-2 shrink-0">{s.qualificacao || 'Sócio'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── FOOTER LINK ── */}
      <div className="flex justify-end pt-2 border-t">
        <button
          type="button"
          onClick={() => onOpenConta?.(conta.id)}
          className="text-xs text-[#00DBFF] hover:text-[#00DBFF]/80 font-bold inline-flex items-center gap-1 cursor-pointer transition-colors"
        >
          <Building2 size={13} />
          Ver ficha completa da conta →
        </button>
      </div>
    </div>
  );
}
